import { Column } from '@qtek/shared/models';
import { isNonNullable, isNullable } from '@qtek/shared/utils';
import {
  combineLatest,
  EMPTY,
  MonoTypeOperatorFunction,
  Observable,
  of,
} from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { ExtendDataTable, QtGroupBy } from '../../models';
import { transformerFactory } from '../transform';
import { ExtendDataTableByMeta } from './group-entity-meta';
import {
  getValueAtPath,
  setValueAtPath,
  summarizeGroup,
} from './grouping-utils';

export function isExtendedDataTable<T>(data: any): data is T & ExtendDataTable {
  return '__groupBy' in data;
}

export function isGroupRowInDataTable<T>(
  data: any
): data is T & ExtendDataTable {
  return '__groupId' in data;
}

export function groupData<T>(
  data: T[],
  groupBy: QtGroupBy<T>,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any,
  columns: Column[] = []
): T[] {
  const columnId = groupBy.columnName;
  const reducedDataGroups = data.reduce((acc, curr) => {
    const currentValue = valueAccessor(curr, columnId);
    if (!acc.includes(currentValue)) {
      return [...acc, currentValue];
    }

    return acc;
  }, []);

  const sortedDataGroups = reducedDataGroups.sort((a, b) => {
    if (isNullable(a) && isNonNullable(b)) {
      return -1;
    } else if (isNullable(b) && isNonNullable(a)) {
      return 1;
    }

    return 0;
  });

  let groupedData: (T | (T & ExtendDataTableByMeta))[] = [];

  sortedDataGroups.forEach(dataGroup => {
    const inGroupElements: T[] = [];

    data.forEach(element => {
      if (isNullable(columnId)) {
        return;
      }

      const currentRow = valueAccessor(element, columnId);

      if (!Array.isArray(currentRow) && currentRow === dataGroup) {
        inGroupElements.push(element);
      }
    });

    const groupSummarized = summarizeGroup(
      inGroupElements,
      valueAccessor,
      columns
    ) as T;

    const firstItem = inGroupElements[0];
    const extractedData = {
      ...((firstItem as any)?.data?.sid && {
        data: {
          sid: (firstItem as any).data.sid,
          data: { ...((groupSummarized as any)?.data?.data || {}) },
        },
      }),
      ...((firstItem as any)?.namespace && {
        namespace: (firstItem as any)?.namespace,
      }),
    };
    const populatedData: any = {};

    // Populate data with groupPropIdPaths like ['usr.acn']
    if (groupBy.groupPropIdPaths) {
      groupBy.groupPropIdPaths.forEach((propIdPath: string) => {
        const value = getValueAtPath(firstItem, propIdPath);
        if (value !== undefined) {
          setValueAtPath(populatedData, propIdPath, value);
        }
      });
    }

    const group: T & ExtendDataTableByMeta = {
      __groupBy: dataGroup,
      __groupById: columnId,
      __totalGroupCount: inGroupElements.length,
      __groupId: 'groupId_' + dataGroup,
      ...extractedData,
      ...populatedData,
      ...groupSummarized,
    };

    const groupElements: T[] = inGroupElements.map(element => {
      return { ...element, __groupBy: dataGroup } as T;
    });

    groupedData = [...groupedData, group, ...groupElements];
  });

  return groupedData as (T | (T & ExtendDataTableByMeta))[];
}

export function qGroupBy<T>(
  groupBy: QtGroupBy<T>,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any,
  columns: Column[] | Observable<Column[]> = []
): MonoTypeOperatorFunction<T[]> {
  if (!groupBy) {
    return transformerFactory<T, Column[]>(EMPTY, ([data]) => data);
  }

  const columns$: Observable<Column[]> = Array.isArray(columns)
    ? of(columns)
    : columns;

  const columnName$ = groupBy.columnName$.pipe(distinctUntilChanged());

  const changes$ = combineLatest([
    columnName$.pipe(startWith(null)),
    columns$.pipe(startWith([])),
  ]);

  return transformerFactory<T, [keyof T | null, Column[]] | null>(
    changes$,
    ([data, changesValue]) => {
      if (!changesValue || !Array.isArray(changesValue)) {
        return data;
      }
      const columnsValue = changesValue[1] ?? [];
      return groupData<T>(data, groupBy, valueAccessor, columnsValue);
    }
  );
}
